'use client';
import { ProductCardContext } from '.';
import { useTranslations } from 'next-intl';
import { ComponentProps, useContext, useMemo } from 'react';

import { Currency } from '@/shared/model/Currency';

import { useCartStore, getPriceValueTranslation } from '@/entities/cart';
import { Layout } from '@/shared/ui/layout';
import { ProductCardProps } from './types';

type Props = ComponentProps<typeof Layout> & Partial<Pick<ProductCardProps, 'priceValues'>>;
export const ProductCardOriginalPrice = ({ priceValues, media, ...props }: Props) => {
    const t = useTranslations();
    const context = useContext(ProductCardContext);
    const { hash, selectedVariantId, priceValues: contextPriceValues } = context;

    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const finalPriceValues = priceValues ?? contextPriceValues;

    const preferredPrice = useMemo(() => {
        const prices = getItemPrices(selectedVariantId || hash);
        if (prices?.itemPrice) {
            const itemPrice = prices.itemPrice as {
                amount: number | string;
                currency: string;
                originalAmount?: number | string;
            };
            return {
                amount: Number(itemPrice.amount),
                currency: itemPrice.currency as Currency,
                originalAmount: itemPrice.originalAmount ? Number(itemPrice.originalAmount) : undefined,
            };
        }

        if (!finalPriceValues || !finalPriceValues.length) {
            return null;
        }

        return {
            amount: Number(finalPriceValues[0].amount),
            currency: finalPriceValues[0].currency as Currency,
            originalAmount: finalPriceValues[0].originalAmount
                ? Number(finalPriceValues[0].originalAmount)
                : undefined,
        };
    }, [getItemPrices, selectedVariantId, hash, finalPriceValues]);

    if (!preferredPrice || !preferredPrice.originalAmount) {
        return null;
    }

    return (
        <Layout
            element="span"
            data-testid="product-card-original-price"
            media={{
                initial: {},
                ...media,
            }}
            {...props}
        >
            {getPriceValueTranslation({
                amount: preferredPrice.originalAmount,
                currency: preferredPrice.currency,
                t,
            })}
        </Layout>
    );
};
